import {
  CalendarPlus,
  Funnel,
  Refresh,
  QuestionCircle,
} from "@reverb-ui/icons-react";
import {
  VStack,
  Button,
  Flex,
  HStack,
  RangePicker,
  Select,
  Text,
  Box,
  Switch,
  Grid,
  GridItem,
  Tooltip,
} from "@reverb-ui/react";
import moment from "moment";
import {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ReportsFilterContext,
  TFilterSelectOption,
  notFoundText,
} from "../../presentation/containers/context-api/ReportsFilterProvider";
import CustomVirtualizedSelect from "./CustomVirtualizedSelect";
import FilterBadge from "./FilterBadge";
import { DownloadPdfButton } from "components/DownloadButton";

const customStyles = {
  container: (provided: any) => ({
    ...provided,
    width: 177,
  }),
};

interface IAllowFilteringProps {}

export const AllowFiltering: React.FC<IAllowFilteringProps> = () => {
  const {
    setComparisonEnabled,
    comparisonEnabled,
    setFiltersIndex,
    filtersIndex,
    setValue,
    resetField,
    resetFields,
    unsavedFilters,
    selectOptions,
    saveFilters,
    loadingOptions,
    loadingRanges,
    loadingDevices,
    fetchInsightsParameters,
    clearAllFilters,
    defaultFiltersLoading,
  } = useContext(ReportsFilterContext);
  const [openRanges, setOpenRanges] = useState<boolean>(false);
  const [openAfterLoad, setOpenAfterLoad] = useState<
    "portfolio" | "campaign" | "creative" | "url" | null
  >(null);
  const portfolioSelectRef = useRef<any>(null);
  const campaignSelectRef = useRef<any>(null);
  const creativeSelectRef = useRef<any>(null);
  const urlSelectRef = useRef<any>(null);

  const rangeSelectRef = useRef<any>(null);
  const deviceSelectRef = useRef<any>(null);

  const refs: Record<
    "portfolio" | "campaign" | "creative" | "url",
    MutableRefObject<any>
  > = {
    portfolio: portfolioSelectRef,
    campaign: campaignSelectRef,
    creative: creativeSelectRef,
    url: urlSelectRef,
  };

  const creativeError = () => {
    return (
      !loadingOptions &&
      unsavedFilters[filtersIndex].url &&
      !unsavedFilters[filtersIndex].creative &&
      selectOptions.creative?.some((x) => x.value)
    );
  };

  const checkAnySelectOption = (options?: TFilterSelectOption[]): boolean => {
    return !!options?.some((x) => x.value != null || undefined);
  };

  const portfolioDisabled =
    (unsavedFilters[filtersIndex].campaign?.value ||
      unsavedFilters[filtersIndex].creative?.value ||
      unsavedFilters[filtersIndex].url?.value) &&
    !unsavedFilters[filtersIndex].portfolio;

  const campaignDisabled =
    (unsavedFilters[filtersIndex].creative?.value ||
      unsavedFilters[filtersIndex].url?.value) &&
    !unsavedFilters[filtersIndex].campaign;

  const creativeDisabled =
    unsavedFilters[filtersIndex].url?.value &&
    !unsavedFilters[filtersIndex].creative &&
    !creativeError();

  const showFilterBadge = comparisonEnabled;

  const actionButtonText = "Update";
  const allPortfoliosLabel = "All Portfolios";
  const allCampaignsLabel = "All Campaigns";
  const allCreativesLabel = "All Creatives";
  const allUrlsLabel = "All URLs";

  const handleActionButton = () => {
    saveFilters();
  };

  const handleChangeComparission = (checked: boolean) => {
    if (!checked) {
      setFiltersIndex(0);
    }

    setComparisonEnabled(checked);
  };

  const handleClear = () => {
    clearAllFilters();
    fetchInsightsParameters();
    setFiltersIndex(0);
  };

  const handleBadgeEdit = (index: number) => {
    if (index !== filtersIndex) {
      setFiltersIndex(index);
    }
  };

  const handleSelectOpen = (selectRef: React.MutableRefObject<any>) => {
    selectRef.current.focus();
    selectRef.current.onMenuOpen();
  };

  const handleSelectOpenAfterLoad = (
    selectName: "portfolio" | "campaign" | "creative" | "url"
  ) => {
    setOpenAfterLoad(selectName);
  };

  useEffect(() => {
    if (!defaultFiltersLoading) {
      fetchInsightsParameters({
        portfolio: unsavedFilters[filtersIndex]?.portfolio,
        campaign: unsavedFilters[filtersIndex]?.campaign,
        creative: unsavedFilters[filtersIndex]?.creative,
        url: unsavedFilters[filtersIndex]?.url,
      });
    }

    if (openRanges) {
      resetField("endDate");
      resetField("startDate");
      handleSelectOpen(rangeSelectRef);
      setOpenRanges(false);
    }
  }, [filtersIndex]);

  useEffect(() => {
    if (openAfterLoad && !loadingOptions) {
      const selectRef = refs[openAfterLoad];

      if (!selectRef.current.props.isDisabled) {
        handleSelectOpen(selectRef);
      }

      setOpenAfterLoad(null);
    }
  }, [loadingOptions]);

  return (
    <Flex flexDir="column" gap={5} marginTop={6} className="filter">
      <Flex justifyContent="end" gap={5} align="center">
        <DownloadPdfButton />
        <Flex justifyContent="end" gap={2}>
          <Switch
            onChange={(e) => handleChangeComparission(e.target.checked)}
            isChecked={comparisonEnabled}
          >
            Compare performances
          </Switch>
          <Tooltip
            label={
              <>
                Compare the performance of experiences, URLs, and campaigns
                against each other, or track performance over time. Select items
                to see a side-by-side comparison of their key performance
                metrics.
                <br />
                <br />
                <strong>Note:</strong> When comparing different levels (e.g., a
                campaign vs. a single experience), only the panels available for
                both selections will be shown.
              </>
            }
            bg="gray.100"
            color="gray.800"
            shouldWrapChildren
            maxWidth="400px"
          >
            <Box pt="4px">
              <QuestionCircle color="gray.400" />
            </Box>
          </Tooltip>
        </Flex>
      </Flex>
      <VStack
        width="100%"
        bgColor="gray.100"
        borderRadius="base"
        py="4"
        px="7"
        spacing={3.5}
        marginBottom={12}
      >
        <HStack alignItems="baseline" width="full" spacing={3.5}>
          <Funnel color="gray.500" />

          <Grid templateColumns="repeat(4, 1fr)" gap={3.5} w="full">
            <GridItem display="flex" flexDir="column" gap={1}>
              <CustomVirtualizedSelect
                ref={refs["portfolio"]}
                isLoading={loadingOptions}
                isDisabled={
                  portfolioDisabled ||
                  !checkAnySelectOption(selectOptions.portfolio)
                }
                options={selectOptions.portfolio || []}
                value={unsavedFilters[filtersIndex].portfolio || null}
                onChange={(value: any) => {
                  setValue("portfolio", value);
                  resetFields(["campaign", "creative", "url"]);
                  fetchInsightsParameters({ portfolio: value });
                }}
                placeholder={
                  !portfolioDisabled || loadingOptions
                    ? allPortfoliosLabel
                    : notFoundText
                }
              />
              {showFilterBadge && (
                <Flex gap={2} width="100%">
                  {unsavedFilters.map((filter, index) => (
                    <FilterBadge
                      index={index}
                      maxWidthText={117}
                      onClickEditIcon={() => {
                        handleBadgeEdit(index);
                        handleSelectOpenAfterLoad("portfolio");
                      }}
                    >
                      {filter.portfolio?.label ?? allPortfoliosLabel}
                    </FilterBadge>
                  ))}
                </Flex>
              )}
            </GridItem>
            <GridItem display="flex" flexDir="column" gap={1}>
              <CustomVirtualizedSelect
                ref={refs["campaign"]}
                isLoading={loadingOptions}
                isDisabled={
                  campaignDisabled ||
                  !checkAnySelectOption(selectOptions.campaign)
                }
                options={selectOptions.campaign || []}
                value={unsavedFilters[filtersIndex].campaign || null}
                onChange={(value: any) => {
                  setValue("campaign", value);
                  resetFields(["creative", "url"]);
                  fetchInsightsParameters({
                    portfolio: unsavedFilters[filtersIndex].portfolio,
                    campaign: value,
                  });
                }}
                placeholder={
                  !campaignDisabled || loadingOptions
                    ? allCampaignsLabel
                    : notFoundText
                }
              />
              {showFilterBadge && (
                <Flex gap={2} width="100%">
                  {unsavedFilters.map((filter, index) => (
                    <FilterBadge
                      index={index}
                      maxWidthText={117}
                      onClickEditIcon={() => {
                        handleBadgeEdit(index);
                        handleSelectOpenAfterLoad("campaign");
                      }}
                    >
                      {filter.campaign?.label ?? allCampaignsLabel}
                    </FilterBadge>
                  ))}
                </Flex>
              )}
            </GridItem>
            <GridItem display="flex" flexDir="column" gap={1}>
              <Box w="100%">
                <CustomVirtualizedSelect
                  ref={refs["creative"]}
                  isLoading={loadingOptions}
                  isDisabled={
                    creativeDisabled ||
                    !checkAnySelectOption(selectOptions.creative)
                  }
                  options={selectOptions.creative || []}
                  value={unsavedFilters[filtersIndex].creative || null}
                  onChange={(value: any) => {
                    setValue("creative", value);
                    !creativeError() && resetField("url");
                    fetchInsightsParameters({
                      portfolio: unsavedFilters[filtersIndex].portfolio,
                      campaign: unsavedFilters[filtersIndex].campaign,
                      creative: value,
                      url: creativeError()
                        ? unsavedFilters[filtersIndex].url
                        : undefined,
                    });
                  }}
                  placeholder={
                    !creativeDisabled || loadingOptions
                      ? allCreativesLabel
                      : notFoundText
                  }
                  hasError={creativeError()}
                />
                {creativeError() && (
                  <Box fontSize="14px" color="danger.500">
                    Select a creative to filter
                  </Box>
                )}
              </Box>
              {showFilterBadge && (
                <Flex gap={2} width="100%">
                  {unsavedFilters.map((filter, index) => (
                    <FilterBadge
                      index={index}
                      maxWidthText={117}
                      onClickEditIcon={() => {
                        handleBadgeEdit(index);
                        handleSelectOpenAfterLoad("creative");
                      }}
                    >
                      {filter.creative?.label ?? allCreativesLabel}
                    </FilterBadge>
                  ))}
                </Flex>
              )}
            </GridItem>
            <GridItem display="flex" flexDir="column" gap={1}>
              <CustomVirtualizedSelect
                ref={refs["url"]}
                isDisabled={!checkAnySelectOption(selectOptions.url)}
                isLoading={loadingOptions}
                options={selectOptions.url || []}
                value={unsavedFilters[filtersIndex].url || null}
                onChange={(value: any) => {
                  setValue("url", value);
                  fetchInsightsParameters({
                    portfolio: unsavedFilters[filtersIndex].portfolio,
                    campaign: unsavedFilters[filtersIndex].campaign,
                    creative: unsavedFilters[filtersIndex].creative,
                    url: value,
                  });
                }}
                placeholder={allUrlsLabel}
              />
              {showFilterBadge && (
                <Flex gap={2} width="100%">
                  {unsavedFilters.map((filter, index) => (
                    <FilterBadge
                      index={index}
                      maxWidthText={117}
                      onClickEditIcon={() => {
                        handleBadgeEdit(index);
                        handleSelectOpenAfterLoad("url");
                      }}
                    >
                      {filter.url?.label ?? allUrlsLabel}
                    </FilterBadge>
                  ))}
                </Flex>
              )}
            </GridItem>
          </Grid>
        </HStack>

        <HStack alignItems="baseline" width="full" spacing={3.5}>
          <CalendarPlus color="gray.500" />
          <Flex flexDirection="column">
            <Flex
              sx={{
                input: {
                  backgroundColor: "white",
                  width: "100%",
                },
              }}
            >
              <style>
                {`
                  .CalendarMonthGrid {
                    z-index: 2 !important;
                  }
                  .DayPickerNavigation{
                    z-index: 3 !important;
                  }
                  .DayPicker_weekHeaders {
                    z-index: 3 !important;
                  }
                `}
              </style>
              <RangePicker
                endDate={
                  !!unsavedFilters[filtersIndex].endDate
                    ? moment(unsavedFilters[filtersIndex].endDate)
                    : null
                }
                endDatePlaceholder="mm/dd/yyyy"
                onChangeEndDate={(date) => {
                  setValue(
                    "endDate",
                    !!date ? moment(date).format("MM/DD/yyyy") : null
                  );
                  if (!!date) {
                    resetField("ranges");
                  }
                }}
                onChangeStartDate={(date) =>
                  setValue(
                    "startDate",
                    !!date ? moment(date).format("MM/DD/yyyy") : null
                  )
                }
                startDate={
                  !!unsavedFilters[filtersIndex].startDate
                    ? moment(unsavedFilters[filtersIndex].startDate)
                    : null
                }
                startDatePlaceholder="mm/dd/yyyy"
                dateFormat="MM/DD/YYYY"
                maxDays={731}
              />
            </Flex>
            {showFilterBadge && (
              <Flex gap={2} width="100%">
                {unsavedFilters.map((filter, index) =>
                  filter.startDate || filter.endDate ? (
                    <Flex gap={2}>
                      {filter.startDate && (
                        <FilterBadge
                          index={index}
                          onClickEditIcon={() => handleBadgeEdit(index)}
                        >
                          {filter.startDate}
                        </FilterBadge>
                      )}
                      <Flex lineHeight={1} color="gray.500">
                        to
                      </Flex>
                      {filter.endDate && (
                        <FilterBadge
                          index={index}
                          onClickEditIcon={() => handleBadgeEdit(index)}
                        >
                          {filter.endDate}
                        </FilterBadge>
                      )}
                    </Flex>
                  ) : (
                    <FilterBadge
                      index={index}
                      onClickEditIcon={() => handleBadgeEdit(index)}
                    >
                      Set Date Range
                    </FilterBadge>
                  )
                )}
              </Flex>
            )}
          </Flex>
          <Flex flexDir="column" gap={1}>
            <Select
              ref={rangeSelectRef}
              styles={customStyles}
              isDisabled={
                !!unsavedFilters[filtersIndex].startDate ||
                !!unsavedFilters[filtersIndex].endDate
              }
              options={selectOptions.ranges || []}
              placeholder="Choose range"
              value={unsavedFilters[filtersIndex].ranges || null}
              isLoading={loadingRanges}
              onChange={(value) => setValue("ranges", value)}
            />
            {showFilterBadge && (
              <Flex gap={2} width="100%">
                {unsavedFilters.map((filter, index) =>
                  filter.ranges ? (
                    <FilterBadge
                      index={index}
                      maxWidthText={60}
                      onClickEditIcon={() => {
                        handleBadgeEdit(index);
                        setTimeout(() => {
                          handleSelectOpen(rangeSelectRef);
                        }, 500);
                      }}
                    >
                      {filter.ranges?.label}
                    </FilterBadge>
                  ) : (
                    <FilterBadge
                      index={index}
                      onClickEditIcon={() => {
                        handleBadgeEdit(index);
                        setOpenRanges(true);
                      }}
                    >
                      Set Range
                    </FilterBadge>
                  )
                )}
              </Flex>
            )}
          </Flex>
          <Flex flexDir="column" gap={1}>
            <Select
              ref={deviceSelectRef}
              styles={customStyles}
              options={selectOptions.devices || []}
              placeholder="Choose device"
              value={unsavedFilters[filtersIndex].devices || null}
              isLoading={loadingDevices}
              onChange={(value) => setValue("devices", value)}
            />
            {showFilterBadge && (
              <Flex gap={2} width="100%">
                {unsavedFilters.map(
                  (filter, index) =>
                    filter.devices && (
                      <FilterBadge
                        index={index}
                        maxWidthText={117}
                        onClickEditIcon={() => {
                          handleBadgeEdit(index);
                          handleSelectOpen(deviceSelectRef);
                        }}
                      >
                        {filter.devices?.label}
                      </FilterBadge>
                    )
                )}
              </Flex>
            )}
          </Flex>
          <Button
            leftIcon={<Refresh color="white" />}
            colorScheme="blue"
            borderRadius="md"
            px="4"
            py="2"
            _hover={{ bg: "blue.600" }}
            _active={{ bg: "blue.700" }}
            _focus={{ bg: "blue.500" }}
            _disabled={{ opacity: 0.4 }}
            type="submit"
            disabled={
              loadingOptions ||
              loadingRanges ||
              loadingDevices ||
              creativeError()
            }
            onClick={handleActionButton}
          >
            <Text fontSize="md" color="white">
              {actionButtonText}
            </Text>
          </Button>
          <Button variant="ghost" onClick={handleClear}>
            <Text fontSize="md" color="blue.600">
              Clear all
            </Text>
          </Button>
        </HStack>
      </VStack>
    </Flex>
  );
};

export default AllowFiltering;
